import React, { Suspense } from 'react';
import { Routes, Route, useLocation } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { Shield } from 'lucide-react';
import Navbar from './components/Layout/Navbar';
import ScrollToTop from './components/Layout/ScrollToTop';
import './styles/global.css';

// Eager load core pages for better performance and SSG hydration
import Home from './pages/Home';
import JsonBeautifier from './pages/tools/JsonBeautifier';
import JsonCompare from './pages/tools/JsonCompare';
import TextCompare from './pages/tools/TextCompare';
import UnitConverter from './pages/tools/UnitConverter';
import PrivacyPolicy from './pages/PrivacyPolicy';
import Footer from './components/Layout/Footer';

// Lazy load heavy tools
const ImageCompressor = React.lazy(() => import('./pages/tools/ImageCompressor'));
const ImageCrop = React.lazy(() => import('./pages/tools/ImageCrop'));
const ImageConverter = React.lazy(() => import('./pages/tools/ImageConverter'));
const ImageToIco = React.lazy(() => import('./pages/tools/ImageToIco'));
const ImageToPdf = React.lazy(() => import('./pages/tools/ImageToPdf'));
const JsonValidator = React.lazy(() => import('./pages/tools/JsonValidator'));
const ColorPicker = React.lazy(() => import('./pages/tools/ColorPicker'));
const PromptBuilder = React.lazy(() => import('./pages/tools/PromptBuilder'));
const JwtDecoder = React.lazy(() => import('./pages/tools/JwtDecoder'));
const Base64EncoderDecoder = React.lazy(() => import('./pages/tools/Base64EncoderDecoder'));
const UrlEncoderDecoder = React.lazy(() => import('./pages/tools/UrlEncoderDecoder'));
const HashGenerator = React.lazy(() => import('./pages/tools/HashGenerator'));

function App() {
  return (
    <>
      <Helmet defaultTitle="OnlineToolSphere - Premium Developer Tools" titleTemplate="%s | OnlineToolSphere">
        <meta name="description" content="Free, secure, and fast client-side developer tools. Prompt Builder, JSON Beautifier, JSON Compare, Text Compare, Image Compressor, Unit Converter, and more." />
        <meta property="og:site_name" content="OnlineToolSphere" />
        <meta property="og:type" content="website" />
        <meta property="og:image" content="https://onlinetoolsphere.com/ots.png" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:image" content="https://onlinetoolsphere.com/ots.png" />
        <link rel="canonical" href={`https://onlinetoolsphere.com${useLocation().pathname}`} />
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "Organization",
            "name": "OnlineToolSphere",
            "url": "https://onlinetoolsphere.com",
            "logo": "https://onlinetoolsphere.com/ots.png"
          })}
        </script>
      </Helmet>
      <ScrollToTop />
      <Navbar />
      <main>
        <Routes>
          <Route path="/" element={<Home />} />
          <Route path="/tools/json-beautifier" element={<JsonBeautifier />} />
          <Route path="/tools/json-compare" element={<JsonCompare />} />
          <Route path="/tools/text-compare" element={<TextCompare />} />
          <Route path="/tools/image-compressor" element={
            <Suspense fallback={<div className="loading-container">Loading...</div>}>
              <ImageCompressor />
            </Suspense>
          } />
          <Route path="/tools/image-crop" element={
            <Suspense fallback={<div className="loading-container">Loading...</div>}>
              <ImageCrop />
            </Suspense>
          } />
          <Route path="/tools/image-converter" element={
            <Suspense fallback={<div className="loading-container">Loading...</div>}>
              <ImageConverter />
            </Suspense>
          } />
          <Route path="/tools/image-to-ico" element={
            <Suspense fallback={<div className="loading-container">Loading...</div>}>
              <ImageToIco />
            </Suspense>
          } />
          <Route path="/tools/image-to-pdf" element={
            <Suspense fallback={<div className="loading-container">Loading...</div>}>
              <ImageToPdf />
            </Suspense>
          } />
          <Route path="/tools/json-validator" element={
            <Suspense fallback={<div className="loading-container">Loading...</div>}>
              <JsonValidator />
            </Suspense>
          } />
          <Route path="/tools/color-picker" element={
            <Suspense fallback={<div className="loading-container">Loading...</div>}>
              <ColorPicker />
            </Suspense>
          } />
          <Route path="/tools/prompt-builder" element={
            <Suspense fallback={<div className="loading-container">Loading...</div>}>
              <PromptBuilder />
            </Suspense>
          } />
          <Route path="/tools/jwt-decoder" element={
            <Suspense fallback={<div className="loading-container">Loading...</div>}>
              <JwtDecoder />
            </Suspense>
          } />
          <Route path="/tools/base64-encode-decode" element={
            <Suspense fallback={<div className="loading-container">Loading...</div>}>
              <Base64EncoderDecoder />
            </Suspense>
          } />
          <Route path="/tools/url-encode-decode" element={
            <Suspense fallback={<div className="loading-container">Loading...</div>}>
              <UrlEncoderDecoder />
            </Suspense>
          } />
          <Route path="/tools/hash-generator" element={
            <Suspense fallback={<div className="loading-container">Loading...</div>}>
              <HashGenerator />
            </Suspense>
          } />
          <Route path="/tools/unit-converter" element={<UnitConverter />} />
          <Route path="/privacy" element={<PrivacyPolicy />} />
        </Routes>
      </main>
      <Footer />
    </>
  );
}


export default App;
